import { useEffect } from "react";

interface WakeLockSentinelLike { release(): Promise<void> }
interface NativeBridge { keepAwake?: (on: boolean) => void }

/**
 * Keeps the screen on while kiosk mode is up: the Wake Lock API on the web (re-requested when the tab becomes visible
 * again, since the browser drops it on hide), the bridge's `keepAwake` on native (**assumed** name, app-native).
 */
export function useWakeLock(enabled = true) {
  useEffect(() => {
    if (!enabled) return;
    let sentinel: WakeLockSentinelLike | null = null;
    let gone = false;
    const bridge = (globalThis as { HelmrNative?: NativeBridge }).HelmrNative;
    bridge?.keepAwake?.(true);
    const acquire = async () => {
      const wl = (navigator as { wakeLock?: { request(type: "screen"): Promise<WakeLockSentinelLike> } }).wakeLock;
      if (!wl || document.visibilityState !== "visible") return;
      try {
        const s = await wl.request("screen");
        if (gone) await s.release().catch(() => undefined);
        else sentinel = s;
      } catch { /* refused (battery saver, no permission): the screen may dim, kiosk keeps working */ }
    };
    void acquire();
    const onVisible = () => { if (document.visibilityState === "visible") void acquire(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      gone = true;
      document.removeEventListener("visibilitychange", onVisible);
      void sentinel?.release().catch(() => undefined);
      bridge?.keepAwake?.(false);
    };
  }, [enabled]);
}
